import React, { useReducer, useContext } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

import { TextField, Divider, Typography, Link } from '@material-ui/core';

import { Auth } from 'aws-amplify';
import { setJwt } from '../../services/httpService';
import { PORTFOLIO_PATH } from '../../routes';
import { UserContext } from '../../context/UserState';

import BtnPrimary from '../common/BtnPrimary';
import CheckboxPrimary from '../common/CheckboxPrimary';
import InputPassword from '../common/inputs/InputPassword';
import IconLock from '../../assets/icon-lock.svg';

const Root = styled.div`
	margin-top: -15px;

	a {
		cursor: pointer;
		color: ${(props) => props.theme.palette.text.link};
		text-decoration: underline;
	}

	.login__actions {
		margin: 20px 0 0 0;
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.login__checkbox {
		font-size: 14px;
		display: flex;
		align-items: center;

		&:not(:first-child) {
			margin-top: 10px;
		}
	}

	.login__submit {
		margin: 20px 0;
		text-align: right;
	}

	.login__recover-pw {
		background-color: transparent;
		border: 0;
		color: ${(props) => props.theme.palette.text.link};
		font-family: ${(props) => props.theme.typography.fontFamily};
		font-size: 14px;
		text-decoration: underline;
		cursor: pointer;

		&:hover {
			text-decoration: none;
		}
	}

	.login__privacy-info {
		padding: 0 0 20px 0;

		.is-heading {
			display: flex;
			align-items: center;
			font-weight: bold;
			margin-bottom: 20px;

			img {
				width: 25px;
				margin-right: 20px;
			}
		}
	}

	.MuiFormControl-root {
		margin: 20px 0 0 0;
	}

	.error {
		margin: 20px 0 10px 0;
		padding: 20px;
		background-color: ${(props) => props.theme.palette.background.error};
		color: ${(props) => props.theme.palette.text.primary};
		font-size: 16px;
		border-radius: 5px;
	}
`;

const FormDivider = styled(Divider)`
	&.MuiDivider-root {
		margin: 40px 0;
		background-color: ${(props) => props.theme.palette.background.secondary};
	}
`;

function loginReducer(state, action) {
	switch (action.type) {
		case 'field': {
			return {
				...state,
				[action.fieldName]: action.payload,
			};
		}
		case 'login': {
			return {
				...state,
				error: '',
			};
		}
		case 'success': {
			return {
				...state,
				username: '',
				password: '',
			};
		}
		case 'error': {
			return {
				...state,
				error: action.error,
				username: '',
				password: '',
			};
		}
		default:
			return state;
	}
}

const initialState = {
	username: '',
	password: '',
	error: '',
};

export default function FormLogin({
	closeLoginModal,
	openRegisterModal,
	openChangePasswordScreen,
}) {
	const [state, dispatch] = useReducer(loginReducer, initialState);
	const { user, setUser } = useContext(UserContext);
	const history = useHistory();

	const { username, password, error, rememberLogin } = state;

	const onSubmit = async (e) => {
		e.preventDefault();

		// eslint-disable-next-line
		rememberLogin
			? Auth.configure({ storage: localStorage })
			: Auth.configure({ storage: sessionStorage });

		try {
			const awsUser = await Auth.signIn(username, password);
			setJwt(awsUser.signInUserSession.idToken.jwtToken);
			setUser({
				...user,
				isLoggedIn: true,
				username: awsUser.username,
				email: awsUser.attributes.email,
				phone: awsUser.attributes['custom:phone'],
				companyName: awsUser.attributes['custom:companyName'],
				firstNameLastName: awsUser.attributes['custom:firstNameLastName'],
				newsletter: awsUser.attributes['custom:Newsletter'] === 'true' ?? true,
			});
			dispatch({ type: 'success' });
			closeLoginModal();
			history.push(`${PORTFOLIO_PATH}`);
		} catch (error) {
			dispatch({ type: 'error', error: error.message });
			console.log('error signing up:', error);
		}
	};

	const onChange = (e) => {
		dispatch({
			type: 'field',
			fieldName: e.target.name,
			payload: e.target.checked,
		});
	};

	const openRegisterForm = (
		<Link
			onClick={() => {
				closeLoginModal();
				openRegisterModal();
			}}
		>
			registriert?
		</Link>
	);

	const togglePasswordRecovery = (
		<button
			type="button"
			className="login__recover-pw"
			onClick={openChangePasswordScreen}
		>
			Haben Sie Ihr Passwort vergessen?
		</button>
	);

	return (
		<Root>
			<form onSubmit={onSubmit}>
				{error && (
					<div className="error">
						<span>{error}</span>
					</div>
				)}
				<TextField
					value={username}
					variant="outlined"
					margin="normal"
					required
					fullWidth
					label="Benutzername oder E-Mail-Adresse"
					onChange={(e) =>
						dispatch({
							type: 'field',
							fieldName: 'username',
							payload: e.currentTarget.value,
						})
					}
				/>
				<InputPassword
					passwordValue={password}
					hideVisibleIcon
					dispatch={(e) =>
						dispatch({
							type: 'field',
							fieldName: 'password',
							payload: e.currentTarget.value,
						})
					}
				/>

				<div className="login__actions">
					<div className="login__checkbox">
						<CheckboxPrimary onChange={onChange} name="rememberLogin" />
						<Typography>Angemeldet bleiben?</Typography>
					</div>
					{togglePasswordRecovery}
				</div>

				<div className="login__submit">
					<BtnPrimary type="submit" variant="contained">
						Anmelden
					</BtnPrimary>
				</div>
			</form>

			<FormDivider />

			<Typography variant="body2">Noch nicht {openRegisterForm}</Typography>

			<FormDivider />

			<div className="login__privacy-info">
				<div className="is-heading">
					<img src={IconLock} alt="Icon Lock" />
					<span>Ihre persönlichen Daten werden geschützt!</span>
				</div>
				<Typography variant="body2">
					Wir gewährleisten den größtmöglichen Schutz Ihrer persönlichen Daten
					und geben sie nicht an Dritte weiter.
				</Typography>
			</div>
		</Root>
	);
}
